import React from 'react';
import { motion } from 'framer-motion';
import { Reveal, StaggerContainer } from '../animations/Reveal';
import {
    FaHandsWash,
    FaHandHoldingHeart,
    FaClock,
    FaSun,
    FaMoon
} from 'react-icons/fa';
import '../../styles/globals.css';

const HowToUseBit = ({ ritualSteps }) => {
    return (
        <div className="space-y-16">
            {/* Header */}
            <div className="text-center space-y-4">
                <Reveal>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-champagne/10 rounded-full">
                        <FaHandsWash className="w-3 h-3 text-champagne" />
                        <span className="text-sm tracking-widest uppercase text-champagne">
              The Ritual
            </span>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <h2 className="text-4xl md:text-5xl font-heading font-light">
                        Your <span className="italic text-sage">Daily</span> Ritual of{' '}
                        <span className="italic text-champagne">Radiance</span>
                    </h2>
                </Reveal>

                <Reveal delay={0.4}>
                    <p className="text-xl text-charcoal/60 max-w-2xl mx-auto">
                        Transform your skincare routine into a moment of self-care and renewal
                    </p>
                </Reveal>
            </div>

            {/* Steps */}
            <StaggerContainer stagger={0.1}>
                <div className="relative">
                    {/* Timeline Line */}
                    <div className="absolute left-8 md:left-1/2 md:transform md:-translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-champagne via-sage to-transparent" />

                    <div className="space-y-12">
                        {ritualSteps.map((step, index) => (
                            <motion.div
                                key={index}
                                variants={{
                                    hidden: { opacity: 0, y: 40 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                                className="relative"
                            >
                                {/* Step Number */}
                                <div className="absolute left-0 md:left-1/2 md:transform md:-translate-x-1/2 -ml-4 w-8 h-8 rounded-full bg-white border-2 border-champagne flex items-center justify-center z-10">
                                    <span className="text-sm font-medium text-champagne">{step.step}</span>
                                </div>

                                <div className={`md:flex items-center gap-12 ${
                                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                                }`}>
                                    {/* Time Indicator */}
                                    <div className={`md:w-1/3 mb-6 md:mb-0 ${
                                        index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'
                                    }`}>
                                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-nude/30 rounded-full">
                                            {step.time.includes('Morning') ? (
                                                <FaSun className="w-3 h-3 text-champagne" />
                                            ) : step.time.includes('Evening') ? (
                                                <FaMoon className="w-3 h-3 text-sage" />
                                            ) : (
                                                <FaClock className="w-3 h-3 text-charcoal/40" />
                                            )}
                                            <span className="text-sm text-charcoal/60">{step.time}</span>
                                        </div>
                                    </div>

                                    {/* Content Card */}
                                    <div className="md:w-2/3">
                                        <div className="bg-white rounded-2xl p-8 shadow-subtle hover:shadow-lift transition-all duration-500">
                                            <div className="flex items-start gap-6">
                                                <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-champagne/10 to-sage/10 flex items-center justify-center">
                                                    {index === 0 ? (
                                                        <FaHandsWash className="w-6 h-6 text-champagne" />
                                                    ) : index === 1 ? (
                                                        <FaHandHoldingHeart className="w-6 h-6 text-sage" />
                                                    ) : (
                                                        <FaClock className="w-6 h-6 text-charcoal/60" />
                                                    )}
                                                </div>

                                                <div className="flex-1">
                                                    <h3 className="text-2xl font-light text-charcoal mb-3">
                                                        {step.title}
                                                    </h3>
                                                    <p className="text-charcoal/70 leading-relaxed">
                                                        {step.description}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Tip */}
                                            {index === 1 && (
                                                <div className="mt-6 pt-6 border-t border-nude/50">
                                                    <p className="text-sm text-charcoal/50 italic">
                                                        Pro tip: Warm the serum between your palms before application for better absorption
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </StaggerContainer>

            {/* Frequency Note */}
            <Reveal delay={0.6}>
                <div className="text-center pt-12">
                    <div className="inline-block p-6 bg-gradient-to-br from-white to-pearl rounded-2xl shadow-subtle">
                        <h4 className="text-lg font-medium text-charcoal mb-3">
                            Recommended Frequency
                        </h4>
                        <div className="flex items-center justify-center gap-8">
                            <div className="text-center">
                                <div className="text-3xl font-light text-champagne mb-1">AM</div>
                                <div className="text-sm text-charcoal/60">Morning Ritual</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-light text-sage mb-1">PM</div>
                                <div className="text-sm text-charcoal/60">Evening Ritual</div>
                            </div>
                        </div>
                        <p className="text-sm text-charcoal/40 mt-4">
                            Visible results typically appear within 2-4 weeks of consistent use
                        </p>
                    </div>
                </div>
            </Reveal>
        </div>
    );
};

export default HowToUseBit;